import { FeatureFlagName } from '@zorro/common-utils';

import { useMonolithQuery } from './useMonolithQuery';

/**
 * Resolves an EMPLOYER-scoped feature flag via the monolith's
 * `featureFlagControllerEmployerOn` endpoint, which evaluates against the
 * server-side Split (Harness) Node SDK.
 *
 * We deliberately do NOT use the browser Split SDK: a per-session browser SDK
 * re-syncs the rollout plan, opens a streaming connection and posts telemetry on
 * every page load of every app — even pages that read no flag — which drove the
 * bulk of our Split client-request volume. Routing reads through the monolith
 * keeps evaluation on the single durable server SDK.
 *
 * Employer-scoped only: `employerId` is the Split targeting key. For employee-
 * or user-scoped flags call the matching endpoint
 * (`featureFlagControllerEmployeeOn` / `featureFlagControllerUserOn`) — passing
 * an employeeId/userId here would evaluate against the wrong subject.
 *
 * `isReady` is false until the value resolves — gate UI on it to avoid rendering
 * the off-branch before the flag is known (see `useLynxPaymentsContext`).
 */
export function useEmployerFeatureFlag(
  flagName: FeatureFlagName,
  employerId?: string
) {
  const {
    data: isOn = false,
    isLoading,
    isError,
    error,
  } = useMonolithQuery({
    method: 'featureFlagControllerEmployerOn',
    params: employerId && [flagName, employerId],
  });

  // A failed lookup must not masquerade as a resolved "off": keep the flag
  // unresolved (isReady stays false, no treatment) and surface the error so
  // callers can distinguish "flag is off" from "we couldn't read the flag".
  const isReady = !isLoading && !isError;

  return {
    isReady,
    isError,
    error,
    isOn,
    treatment: isReady ? (isOn ? 'on' : 'off') : undefined,
  };
}
